import {
  EnvelopeSimple,
  CalendarBlank,
  MagnifyingGlass,
  ChatTeardropDots,
  Bell,
  Wrench,
  ClipboardText,
  Sparkle,
  type Icon,
} from '@phosphor-icons/react';

// Shared helpers for inbox briefing surfaces (row, drawer, chat modal).
// Extracted from briefing-row.tsx / briefing-drawer.tsx where they were
// duplicated verbatim — keep this the single source of truth.

const NOISE_KEYS = new Set(['runId', 'run_id', 'jobId', 'job_id', 'started', 'ok', 'success', 'id']);
const BODY_KEYS = ['message', 'summary', 'text', 'output', 'content', 'result', 'body'];

export type ParsedResult =
  /**
   * `format` tells a renderer whether the body is prose it may format, or the
   * pretty-printed-JSON fallback below — which has no `raw` twin to fall back
   * on, and whose indentation is the only thing making it readable (FRONT-1250).
   */
  | { kind: 'text'; body: string; format: 'prose' | 'json' }
  | { kind: 'structured'; body: string; meta: Array<[string, string]>; raw: string };

/**
 * Pull human-readable prose (and any metadata fields) out of a cron/skill
 * result that may be raw JSON. Returns plain text untouched when the result
 * isn't JSON.
 */
export function parseResult(raw: string): ParsedResult {
  const trimmed = raw.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return { kind: 'text', body: raw, format: 'prose' };
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    return { kind: 'text', body: raw, format: 'prose' };
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { kind: 'text', body: JSON.stringify(parsed, null, 2), format: 'json' };
  }
  const obj = parsed as Record<string, unknown>;
  let body = '';
  for (const key of BODY_KEYS) {
    const v = obj[key];
    if (typeof v === 'string' && v.trim()) {
      body = v;
      break;
    }
  }
  const meta: Array<[string, string]> = [];
  for (const [k, v] of Object.entries(obj)) {
    if (BODY_KEYS.includes(k)) continue;
    if (NOISE_KEYS.has(k)) {
      if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
        meta.push([k, String(v)]);
      }
      continue;
    }
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
      meta.push([k, String(v)]);
    } else {
      meta.push([k, JSON.stringify(v)]);
    }
  }
  if (!body) {
    return { kind: 'text', body: JSON.stringify(obj, null, 2), format: 'json' };
  }
  return { kind: 'structured', body, meta, raw: JSON.stringify(obj, null, 2) };
}

/** Pick a phosphor icon for a briefing based on its task type / category. */
export function iconForTask(taskType: string, category: string): Icon {
  const key = (taskType || category || '').toLowerCase();
  if (key.includes('mail') || key.includes('email')) return EnvelopeSimple;
  if (key.includes('calendar') || key.includes('invite')) return CalendarBlank;
  if (key.includes('research') || key.includes('search') || key.includes('query')) return MagnifyingGlass;
  if (key.includes('chat') || key.includes('message')) return ChatTeardropDots;
  if (key.includes('alert') || key.includes('warn')) return Bell;
  if (key.includes('maintenance') || key.includes('fix')) return Wrench;
  if (key.includes('brief') || key.includes('report')) return ClipboardText;
  return Sparkle;
}

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Format a Date as a UTC iCalendar timestamp (YYYYMMDDTHHMMSSZ). */
export function toIcsDate(d: Date): string {
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}

/**
 * Trigger a client-side download of an already-built Blob — the
 * create-link-click-revoke dance, in one place. FRONT-1809: pulled out of
 * `downloadBlob` below so a caller that already has a fetched Blob (the
 * Sketches download flow, `~/features/sketches/sketch-download.ts`, and
 * since FRONT-2024 the shared Apps download flow,
 * `~/features/apps/app-download.ts`) doesn't have to re-wrap it in a
 * `new Blob([...])` just to reuse this sequence.
 */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Trigger a client-side download of in-memory text content. */
export function downloadBlob(content: string, filename: string, mime: string): void {
  saveBlob(new Blob([content], { type: `${mime};charset=utf-8` }), filename);
}

// FRONT-817: the Snooze menu's three options, as pure helpers so the date
// math is unit-testable without mounting a row (same reasoning as
// `splitFeedSelection` in briefing-feed.tsx — this harness has no jsdom, so
// anything that must be exercised by a real click stays untested; pull the
// logic out into a plain function instead).

export type SnoozeChoice = 'tomorrow' | 'next-week' | 'never';

/**
 * Local midnight of the day after `now` — the "Tomorrow" snooze option, in
 * the viewer's local timezone (per FRONT-817 spec), returned as an ISO
 * datetime string for the `until` field of POST /api/feed/snooze.
 */
export function startOfTomorrowISO(now: Date = new Date()): string {
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0).toISOString();
}

/** Exactly 7 days from `now` — the "Next week" snooze option. */
export function sevenDaysFromNowISO(now: Date = new Date()): string {
  return new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
}

/** Map a Snooze-menu choice to the `until` value POST /api/feed/snooze expects. */
export function untilForSnoozeChoice(choice: SnoozeChoice, now: Date = new Date()): string {
  if (choice === 'tomorrow') return startOfTomorrowISO(now);
  if (choice === 'next-week') return sevenDaysFromNowISO(now);
  return 'never';
}

/** Confirmation-toast copy for a given snooze choice. */
export function toastMessageForSnoozeChoice(choice: SnoozeChoice): string {
  if (choice === 'tomorrow') return 'Snoozed until tomorrow';
  if (choice === 'next-week') return 'Snoozed until next week';
  return 'Hidden from inbox';
}
